import { Loader } from '@oy/uidesktop';
import { Navigate } from 'react-router-dom';
import { MainLayout } from 'src/components/Layouts';
import { PATH } from 'src/constants/Path';
import { useDataActivation, usePathValidation, useRoute } from 'src/hooks';
import { MainRouteProps } from './types';

/**
 * The default for Route is need Auth to be accessed
 * You can add @param publicAccess for Public Page
 *
 * @remark Remember to make validation in usePathValidation!
 *
 * @example
 * Here's a simple example:
 * ```
 * // Public Route e.g: register, login, etc
 * <MainRoute
 *    publicAccess
 *  {...props)
 *  />
 * ```
 * @example
 * Here's an example with negative numbers:
 * ```
 * // Default
 * <MainRoute
 *  {...props)
 *  />
 * ```
 */
const MainRoute = ({
  element,
  pageTitle,
  actionButton,
  backButton,
  backButtonUrl,
  publicAccess = false,
  showSideBar = true,
  showHeader = true,
  showMessenger = true,
  upgradeAlert,
  helpCategoryIndex,
  subCategoryPrefix,
}: MainRouteProps) => {
  const { routeIsActive } = usePathValidation();
  const { isLoading: isLoadingDataActivation } = useDataActivation();
  const { state, redirectUrl } = useRoute();
  const { isRedirect, isUserLoggedOut, isUserLoggedIn, isLoading, pathname } =
    state;

  if (isRedirect || (!publicAccess && isUserLoggedOut))
    return <Navigate to={redirectUrl()} replace />;
  if (
    (publicAccess &&
      isUserLoggedIn &&
      pathname !== PATH.PUBLIC.inputNewPassword) ||
    (!publicAccess &&
      !isLoading &&
      !isLoadingDataActivation &&
      !routeIsActive(pathname))
  )
    return <Navigate to={PATH.root} replace />;

  if (publicAccess) return <>{element}</>;

  return (
    <MainLayout
      pageTitle={pageTitle}
      actionButton={actionButton}
      backButton={backButton}
      backButtonUrl={backButtonUrl}
      showSideBar={showSideBar}
      showHeader={showHeader}
      showMessenger={showMessenger}
      helpCategoryIndex={helpCategoryIndex}
      subCategoryPrefix={subCategoryPrefix}
      upgradeAlert={isLoading ? undefined : upgradeAlert}
    >
      {isLoading || isLoadingDataActivation ? <Loader /> : element}
    </MainLayout>
  );
};

export default MainRoute;
